/**
 * AnalyticsConfigCard — Super-admin only Google Analytics 4 setup.
 * Rendered in Admin Setup → Analytics tab.
 */
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Bug, CheckCircle2, ExternalLink, Facebook, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

interface AnalyticsConfig {
  ga_measurement_id: string;
  ga_enabled: boolean;
  ga_debug_mode: boolean;
  meta_pixel_id: string;
  meta_pixel_enabled: boolean;
  updated_at: string;
}

const GA_ID_RE = /^G-[A-Z0-9]{4,20}$/;
const META_PIXEL_RE = /^[0-9]{6,20}$/;

export const AnalyticsConfigCard: React.FC = () => {
  const qc = useQueryClient();
  const [gaId, setGaId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [debugMode, setDebugMode] = useState(false);
  const [pxId, setPxId] = useState("");
  const [pxEnabled, setPxEnabled] = useState(true);
  const [touched, setTouched] = useState(false);

  const cfg = useQuery<AnalyticsConfig>({
    queryKey: ["admin-analytics"],
    queryFn: async () => (await api.get("/admin/analytics")).data,
  });

  useEffect(() => {
    if (cfg.data) {
      setGaId(cfg.data.ga_measurement_id || "");
      setEnabled(cfg.data.ga_enabled ?? true);
      setDebugMode(cfg.data.ga_debug_mode ?? false);
      setPxId(cfg.data.meta_pixel_id || "");
      setPxEnabled(cfg.data.meta_pixel_enabled ?? true);
    }
  }, [cfg.data]);

  const save = useMutation({
    mutationFn: async () =>
      (await api.put("/admin/analytics", {
        ga_measurement_id: gaId.trim(),
        ga_enabled: enabled,
        ga_debug_mode: debugMode,
        meta_pixel_id: pxId.trim(),
        meta_pixel_enabled: pxEnabled,
      })).data,
    onSuccess: () => {
      toast.success("Analytics config saved");
      setTouched(false);
      qc.invalidateQueries({ queryKey: ["admin-analytics"] });
      try { sessionStorage.removeItem("sb_ga_config_v1"); } catch {}
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const sendTest = useMutation({
    mutationFn: async () => {
      // Fire client-side gtag + fbq events (server sees real UA/cookies), then
      // log the audit entry server-side.
      const now = new Date().toISOString();
      const params = { debug_mode: true, event_time: now, source: "admin_test_button" };
      try { if ((window as any).gtag) (window as any).gtag("event", "sb_test_event", params); } catch {}
      try { if ((window as any).fbq) (window as any).fbq("trackCustom", "SociBabaTestEvent", params); } catch {}
      return (await api.post("/admin/analytics/test-event", {})).data;
    },
    onSuccess: (d: any) => toast.success(d?.message || "Test event fired"),
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Could not send"),
  });

  const idValid = !gaId.trim() || GA_ID_RE.test(gaId.trim());
  const pxValid = !pxId.trim() || META_PIXEL_RE.test(pxId.trim());

  return (
    <div className="sg-card p-6" data-testid="admin-analytics-card">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-[color:var(--sb-saffron)]/10 flex items-center justify-center">
            <BarChart3 className="h-4 w-4 text-[color:var(--sb-saffron)]" />
          </div>
          <div>
            <div className="font-display text-lg font-bold tracking-tight">Analytics & Attribution</div>
            <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">
              Google Analytics 4 + Meta Pixel. Auto-tracks page views, e-commerce funnel & UTM sources. Super-admin only.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {cfg.data?.ga_enabled && (
            <span className="sg-chip bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]" data-testid="ga-live-chip">
              <CheckCircle2 className="h-3 w-3" /> GA4
            </span>
          )}
          {cfg.data?.meta_pixel_enabled && (
            <span className="sg-chip bg-blue-50 text-blue-700 border-blue-200 text-[10px]" data-testid="px-live-chip">
              <CheckCircle2 className="h-3 w-3" /> Pixel
            </span>
          )}
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <Label htmlFor="ga-id">Google Analytics 4 Measurement ID</Label>
          <Input
            id="ga-id"
            value={gaId}
            onChange={(e) => { setGaId(e.target.value); setTouched(true); }}
            placeholder="G-XXXXXXXXXX"
            className="mt-1 font-mono text-sm"
            data-testid="ga-measurement-id"
          />
          {!idValid && (
            <div className="text-xs text-red-600 mt-1">
              Must start with G- and contain uppercase letters and digits.
            </div>
          )}
          <div className="text-xs text-[color:var(--sg-muted)] mt-2">
            Find yours at{" "}
            <a href="https://analytics.google.com/analytics/web/#/a/admin" target="_blank" rel="noreferrer"
               className="text-[color:var(--sb-saffron)] hover:underline inline-flex items-center gap-1">
              analytics.google.com → Admin → Data Streams <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </div>
        </div>

        <div className="rounded-lg border border-[color:var(--sg-border)] p-4 flex items-center justify-between">
          <div>
            <div className="font-semibold text-sm">Enable GA4 tracking</div>
            <div className="text-xs text-[color:var(--sg-muted)]">
              Turn off temporarily without deleting your ID.
            </div>
          </div>
          <Switch checked={enabled} onCheckedChange={(v) => { setEnabled(v); setTouched(true); }}
                  disabled={!gaId.trim()} data-testid="ga-enabled-switch" />
        </div>

        <div className="rounded-lg border border-[color:var(--sg-border)] p-4 flex items-center justify-between">
          <div>
            <div className="font-semibold text-sm inline-flex items-center gap-1.5"><Bug className="h-3.5 w-3.5 text-amber-600" /> Debug mode</div>
            <div className="text-xs text-[color:var(--sg-muted)]">
              Sends <code>debug_mode:true</code> so events appear in GA4 Debug View instantly.
            </div>
          </div>
          <Switch checked={debugMode} onCheckedChange={(v) => { setDebugMode(v); setTouched(true); }}
                  disabled={!gaId.trim()} data-testid="ga-debug-switch" />
        </div>

        <div className="border-t border-[color:var(--sg-border)] pt-5">
          <Label htmlFor="px-id" className="inline-flex items-center gap-1.5"><Facebook className="h-3.5 w-3.5 text-blue-600" /> Meta Pixel ID</Label>
          <Input
            id="px-id"
            value={pxId}
            onChange={(e) => { setPxId(e.target.value); setTouched(true); }}
            placeholder="1234567890123456"
            className="mt-1 font-mono text-sm"
            data-testid="meta-pixel-id"
          />
          {!pxValid && (
            <div className="text-xs text-red-600 mt-1">Meta Pixel ID must be 6–20 digits.</div>
          )}
          <div className="text-xs text-[color:var(--sg-muted)] mt-2">
            Find yours at{" "}
            <a href="https://business.facebook.com/events_manager2" target="_blank" rel="noreferrer"
               className="text-[color:var(--sb-saffron)] hover:underline inline-flex items-center gap-1">
              Meta Events Manager → Data Sources → Pixel <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </div>
        </div>

        <div className="rounded-lg border border-[color:var(--sg-border)] p-4 flex items-center justify-between">
          <div>
            <div className="font-semibold text-sm">Enable Meta Pixel</div>
            <div className="text-xs text-[color:var(--sg-muted)]">
              Mirrors the funnel (ViewContent, AddToCart, Purchase) to Meta Ads.
            </div>
          </div>
          <Switch checked={pxEnabled} onCheckedChange={(v) => { setPxEnabled(v); setTouched(true); }}
                  disabled={!pxId.trim()} data-testid="px-enabled-switch" />
        </div>

        <div className="rounded-lg bg-[color:var(--sb-saffron)]/5 border border-[color:var(--sb-saffron)]/20 p-4 text-xs text-[color:var(--sg-muted)]">
          <div className="font-semibold text-[color:var(--sg-fg)] mb-1">Auto-tracked events</div>
          <ul className="list-disc ml-4 space-y-0.5">
            <li>page_view — on every SPA route change</li>
            <li>login / sign_up — with method (google, facebook, linkedin, password, passkey)</li>
            <li>view_item, select_item, view_item_list, search — product surfaces</li>
            <li>add_to_cart, remove_from_cart, begin_checkout, add_shipping_info, add_payment_info</li>
            <li>purchase, refund — checkout flow (mirrored to Pixel as Purchase)</li>
            <li>share — social share widget</li>
          </ul>
        </div>

        <div className="flex justify-between items-center pt-2 gap-3 flex-wrap">
          <Button
            onClick={() => sendTest.mutate()}
            disabled={sendTest.isPending || (!gaId.trim() && !pxId.trim())}
            variant="outline"
            data-testid="analytics-test-btn"
            className="border-[color:var(--sg-border)] hover:border-[color:var(--sb-saffron)]"
          >
            {sendTest.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
            Send test event
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || !touched || !idValid || !pxValid}
            className="bg-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron-hover)] text-white"
            data-testid="ga-save-btn"
          >
            {save.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Save analytics config
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsConfigCard;
